import {NotFoundPage} from 'common';
import React from 'react';
import {Helmet} from 'react-helmet';
import {ProviderPageTitle} from './component/ProviderPageTitle';
import {Route, Switch} from 'react-router';
import {OktaAuthenticatedEnvironment, PortalSecureRoute} from 'security';
import {TermOfServiceGuard} from 'termofservice/TermOfServiceGuard';

import {SessionStoredReferralPage} from './core/persistence/SessionStoredReferralPage';
import {NewOfferPage} from './NewOfferPage';
import {ViewEditOfferPage} from './ViewEditOfferPage';
import {ClientProfilePage} from "./client/ClientProfilePage";
import {registerPlugin} from 'react-filepond';
import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size';
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import {FilePondPluginFileNameMiddleEllipsis} from './client/filePondMiddleEllipsis';
import {ParentCasePage} from "./client/ParentCasePage";
import {AuthorizationListPage} from "./client/authorization/AuthorizationListPage";
import {SubmittedRequestListPage} from "./client/submitted-request/SubmittedRequestListPage";
import {ProviderEventUnreadCount} from "./notification/ProviderEventUnreadCount";
import {ProviderEventListPage} from "./notification/ProviderEventListPage";
import {ProviderConditionalRedirect} from "./component/ProviderConditionalRedirect";
import {ClientOfferAtRiskPage} from "./client/offer-at-risk/ClientOfferAtRiskPage";
import {ProviderMetricsPage} from "./client/provider-metrics/ProviderMetricsPage";
import {RosterManagementPage} from "./client/roster/RosterManagementPage";
import {AnticipatedDischargePage} from './client/discharge/AnticipatedDischargePage';
import {BusinessContactPage} from "./client/business-contact/BusinessContactPage";
import {ExportAuditLogPage} from "./client/audit/ExportAuditLogPage";
import {ProgressPortraitDownloadAuditPage} from "./client/audit/ProgressPortraitDownloadAuditPage";
import {useApplicationRootUrl} from 'common/component/ApplicationRootContext';
import {PortalFeatureRoute} from './component/PortalFeatureRoute';
import {hasClientReportAccess, hasProviderAccess, hasProviderMetricsAccess, hasRosterAccess} from './domain/security';
import {canAccessAnticipatedDischarge, canAccessOfferAtRisk} from './domain/accessMap';


const PROVIDER_TEAM = [
    'PROVIDER',
] as const;

registerPlugin(FilePondPluginFileValidateSize, FilePondPluginFileValidateType, FilePondPluginFileNameMiddleEllipsis);

export const ProviderApp = () => {
    const rootUrl = useApplicationRootUrl();

    return (
        <OktaAuthenticatedEnvironment>
            <Helmet>
                <meta
                    name="description"
                    content="Client Placement - The Catalight Care Services Provider Portal"
                />
            </Helmet>
            <ProviderPageTitle/>
            <TermOfServiceGuard>
                <ProviderEventUnreadCount>
                    <Switch>
                        <PortalFeatureRoute requiredAccess={hasProviderAccess} path={`${rootUrl}/offer/new`}
                                           exact
                                           render={() => <NewOfferPage/>}/>
                        <PortalFeatureRoute requiredAccess={hasProviderAccess} path={`${rootUrl}/offer/:offerGuid`}
                                           render={() => <ViewEditOfferPage/>}/>
                        <PortalFeatureRoute requiredAccess={hasProviderAccess} path={`${rootUrl}/referral`}
                                           render={() => <SessionStoredReferralPage/>}/>
                        <PortalFeatureRoute requiredAccess={hasClientReportAccess} path={`${rootUrl}/clients/:clientGuid`}
                                           render={() => <ClientProfilePage isAzure={false}/>}/>
                        <PortalFeatureRoute requiredAccess={hasClientReportAccess} path={`${rootUrl}/clients`}
                                           render={() => <ParentCasePage/>}/>
                        <PortalFeatureRoute requiredAccess={hasClientReportAccess} path={`${rootUrl}/notifications`}
                                           render={() => <ProviderEventListPage/>}/>
                        <PortalFeatureRoute requiredAccess={hasClientReportAccess} path={`${rootUrl}/authorizations`}
                                           render={() => <AuthorizationListPage/>}/>
                        <PortalFeatureRoute requiredAccess={canAccessOfferAtRisk} path={`${rootUrl}/clientOfferAtRisk`}
                                           render={() => <ClientOfferAtRiskPage/>}/>
                        <PortalFeatureRoute requiredAccess={hasClientReportAccess} path={`${rootUrl}/submittedRequests`}
                                           render={() => <SubmittedRequestListPage/>}/>
                        <PortalFeatureRoute requiredAccess={hasProviderMetricsAccess} path={`${rootUrl}/providerMetrics`}
                                           render={() => <ProviderMetricsPage/>}/>
                        <PortalFeatureRoute requiredAccess={hasRosterAccess} path={`${rootUrl}/roster`}
                                           render={() => <RosterManagementPage/>}/>
                        <PortalFeatureRoute destination='businessContacts' path={`${rootUrl}/contacts`}
                                           render={() => <BusinessContactPage/>}/>
                        <PortalFeatureRoute destination='auditExport' path={`${rootUrl}/audit/export`}
                                           render={() => <ExportAuditLogPage/>}/>
                        <PortalFeatureRoute destination='progressPortraitDownloads' path={`${rootUrl}/audit/progress-portrait-downloads`}
                                           render={() => <ProgressPortraitDownloadAuditPage/>}/>
                        <PortalFeatureRoute requiredAccess={canAccessAnticipatedDischarge} path={`${rootUrl}/anticipatedDischarges`}
                                           render={() => <AnticipatedDischargePage/>}/>
                        <PortalSecureRoute allowedRoles={PROVIDER_TEAM} path={`${rootUrl}`}
                                           exact
                                           render={() => <ProviderConditionalRedirect/>}/>
                        <Route path='*' component={NotFoundPage}/>
                    </Switch>
                </ProviderEventUnreadCount>
            </TermOfServiceGuard>
        </OktaAuthenticatedEnvironment>
    );
}
